import { decode } from 'base64-arraybuffer';
import * as ImagePicker from 'expo-image-picker';

import { supabase } from '@/lib/supabase';
import type { VerificationDocType, VerificationRequest } from '@/lib/database.types';

/**
 * Επιλέγει έγγραφο ταυτότητας (φωτογραφία), το ανεβάζει στο ΙΔΙΩΤΙΚΟ bucket
 * `verifications` (κρυπτογραφημένο at-rest) και δημιουργεί αίτημα επαλήθευσης.
 * Επιστρέφει το αίτημα, ή null αν ακυρώθηκε.
 */
export async function submitVerification(
  userId: string,
  docType: VerificationDocType,
): Promise<VerificationRequest | null> {
  // Χωρίς requestMediaLibraryPermissionsAsync() — βλ. σχόλιο στο avatar.ts.
  const result = await ImagePicker.launchImageLibraryAsync({
    mediaTypes: ImagePicker.MediaTypeOptions.Images,
    quality: 0.7,
    base64: true,
  });

  const asset = result.assets?.[0];
  if (result.canceled || !asset?.base64) return null;

  const ext = (asset.uri.split('.').pop() || 'jpg').toLowerCase().split('?')[0];
  const contentType = asset.mimeType ?? `image/${ext === 'jpg' ? 'jpeg' : ext}`;
  const path = `${userId}/${docType}-${Date.now()}.${ext}`;

  const { error: upErr } = await supabase.storage
    .from('verifications')
    .upload(path, decode(asset.base64), { contentType, upsert: true });
  if (upErr) throw upErr;

  const { data, error } = await supabase
    .from('verification_requests')
    .insert({ user_id: userId, document_path: path, doc_type: docType })
    .select('*')
    .single();
  if (error) throw error;
  return data;
}

/** Το πιο πρόσφατο αίτημα επαλήθευσης του χρήστη (ή null). */
export async function getMyVerification(userId: string): Promise<VerificationRequest | null> {
  const { data, error } = await supabase
    .from('verification_requests')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return data;
}

/** (Admin) Λίστα εκκρεμών αιτημάτων επαλήθευσης. RLS επιτρέπει μόνο σε admin. */
export async function listPendingVerifications(): Promise<VerificationRequest[]> {
  const { data, error } = await supabase
    .from('verification_requests')
    .select('*')
    .eq('status', 'pending')
    .order('created_at', { ascending: true });
  if (error) throw error;
  return data ?? [];
}

/** Ελαφρύ count (χωρίς δεδομένα) — για badge ένδειξη εκκρεμών αιτημάτων επαλήθευσης. */
export async function countPendingVerifications(): Promise<number> {
  const { count, error } = await supabase
    .from('verification_requests')
    .select('*', { count: 'exact', head: true })
    .eq('status', 'pending');
  if (error) throw error;
  return count ?? 0;
}

/** (Admin) Προσωρινό signed URL για να δει ο admin το έγγραφο (ιδιωτικό bucket). */
export async function getVerificationDocUrl(path: string): Promise<string> {
  const { data, error } = await supabase.storage
    .from('verifications')
    .createSignedUrl(path, 120);
  if (error) throw error;
  return data.signedUrl;
}

/** (Admin) Έγκριση ή απόρριψη αιτήματος (μέσω SECURITY DEFINER RPC). */
export async function reviewVerification(
  requestId: string,
  approve: boolean,
  note?: string,
): Promise<void> {
  const { error } = await supabase.rpc('review_verification', {
    request_id: requestId,
    approve,
    note: note ?? undefined,
  });
  if (error) throw error;
}
